import { applyCommand, type Command } from "../document/commands.ts";
import { playersAbove } from "../document/band.ts";
import { validateDocument } from "../document/layout-rules.ts";
import { COLLISION_CELLS_PER_TILE, type MapAtmosphere, type MapDocument, type MapHost, type MapShare, type WorldDocument } from "../document/types.ts";
import type { ZoneTypeDefinition } from "./adapter.ts";
import { arrivalName } from "./door-links.ts";
import { allocateMapId, mapNameRefusal, newMapDocument } from "./map-edits.ts";
import type { AtmosphereLists } from "./room-registration.ts";

/**
 * Rooms in the editor (ADR-122 decisions 1.2, 1.3, 1.4, 3.1 and 9): a map with a `host` stands off the grid, behind
 * a door; the rules a new room's size and host follow, the placed maps and arrival points a host is picked from, the
 * new room as one command that one undo takes back whole, and the host, the sharing and the atmosphere of a map that
 * exists, each change one command.
 */

/**
 * The longest side of a room, in tiles (ADR-122 decision 1.2).
 */
export const ROOM_TILES_MOST = 256;

/**
 * The sharings in the order the pickers list them.
 */
export const SHARES: readonly MapShare[] = ["open", "party", "solo", "owner"];

/**
 * One arrival point of a map as a picker lists it: the entity's id and the name it carries in its `name` field.
 */
export interface ArrivalChoice {
  readonly id: string;
  readonly name: string;
}

/**
 * A map a host can be picked on: a map standing on the grid and the arrival points it holds.
 */
export interface HostChoice {
  readonly map: string;
  readonly name: string;
  readonly arrivals: readonly ArrivalChoice[];
}

/**
 * What a new room is made from.
 */
export interface RoomPlan {
  readonly name: string;
  readonly chunksWide: number;
  readonly chunksHigh: number;
  readonly host: MapHost | undefined;
  readonly share: MapShare;
  readonly floor: boolean;
  readonly atmosphere?: MapAtmosphere;
}

/**
 * What `RoomEdits` needs from the store.
 */
export interface RoomEditsHost {
  readonly document: () => WorldDocument;
  readonly open: (mapId: string) => boolean;
  readonly forget: (mapId: string) => void;
  readonly run: (command: Command) => boolean;
  readonly allocateId: (prefix: string) => string;
  readonly idTag: () => string | null;
  readonly arrivalType: () => string | undefined;
  readonly roomZone: () => ZoneTypeDefinition | null;
  readonly atmosphereLists: () => AtmosphereLists;
}

/**
 * The arrival points a map holds.
 *
 * @param map The map.
 * @param arrivalType The entity type the game treats as an arrival point; none when the game names none.
 * @returns The arrival points in layer order.
 */
export function arrivalsOf(map: MapDocument, arrivalType: string | undefined): ArrivalChoice[] {
  if (arrivalType === undefined) {
    return [];
  }
  return map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((entity) => entity.type === arrivalType).map((entity) => ({ id: entity.id, name: arrivalName(entity) })) : []));
}

/**
 * The most chunks along a room's side: `ROOM_TILES_MOST` tiles in whole chunks.
 *
 * @param chunkSize The world's chunk size in tiles.
 * @returns The count.
 */
export function roomChunksMost(chunkSize: number): number {
  return Math.floor(ROOM_TILES_MOST / chunkSize);
}

/**
 * Why a room's size is refused, or null: each side a whole number of chunks from 1 to `roomChunksMost`.
 *
 * @param chunksWide Width in chunks.
 * @param chunksHigh Height in chunks.
 * @param chunkSize The world's chunk size in tiles.
 * @returns The reason, or null.
 */
export function roomSizeRefusal(chunksWide: number, chunksHigh: number, chunkSize: number): string | null {
  const most = roomChunksMost(chunkSize);
  const side = (value: number): boolean => Number.isInteger(value) && value >= 1 && value <= most;
  if (!side(chunksWide)) {
    return `Width is a whole number of chunks from 1 to ${most}`;
  }
  return side(chunksHigh) ? null : `Height is a whole number of chunks from 1 to ${most}`;
}

/**
 * The refusal of a world: the rule of `validateDocument` it breaks, without the map's own prefix.
 *
 * @param world The world after a command.
 * @param mapId The map the command changed.
 * @returns The rule's words, or null when the world is whole.
 */
function breach(world: WorldDocument, mapId: string): string | null {
  try {
    validateDocument(world);
    return null;
  } catch (error) {
    return plainly(error, mapId);
  }
}

/**
 * A refusal's words without the map's own prefix.
 *
 * @param error What was thrown.
 * @param mapId The map the refusal is about.
 * @returns The words.
 */
function plainly(error: unknown, mapId: string): string {
  const words = error instanceof Error ? error.message : String(error);
  return words.startsWith(`map ${mapId}: `) ? words.slice(`map ${mapId}: `.length) : words;
}

/**
 * The commands that make a map a room, or change the host of one: taken off the grid when it stands on it, then
 * hosted; a map that loses its host is shared by everyone again, since only a room is shared otherwise.
 *
 * @param map The map.
 * @param host The host, or undefined to take the map off its host.
 * @returns The commands in order.
 */
function hostCommands(map: MapDocument, host: MapHost | undefined): Command[] {
  const commands: Command[] = [];
  if (host !== undefined && map.place !== undefined) {
    commands.push({ kind: "placeMap", mapId: map.id, before: map.place });
  }
  if (host === undefined && map.share !== undefined) {
    commands.push({ kind: "setMapShare", mapId: map.id, before: map.share, after: "open" });
  }
  commands.push({ kind: "setMapHost", mapId: map.id, ...(map.host === undefined ? {} : { before: map.host }), ...(host === undefined ? {} : { after: host }) });
  return commands;
}

/**
 * The store's rooms: the choices a host is picked from, a new room, and the host, sharing, capacity and atmosphere
 * of a map that exists.
 */
export class RoomEdits {
  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: RoomEditsHost) {}

  /**
   * Whether the game names an arrival type, so a room can be hosted at all.
   *
   * @returns True when it does.
   */
  public get hostable(): boolean {
    return this.host.arrivalType() !== undefined;
  }

  /**
   * The maps a host can stand on: those on the grid, each with its arrival points.
   *
   * @param except A map left out, the one being hosted.
   * @returns The choices in the document's order.
   */
  public hostChoices(except?: string): HostChoice[] {
    const type = this.host.arrivalType();
    return this.host
      .document()
      .maps.filter((map) => map.place !== undefined && map.id !== except)
      .map((map) => ({ map: map.id, name: map.name, arrivals: arrivalsOf(map, type) }));
  }

  /**
   * The arrival points a map holds, for a door's destination.
   *
   * @param mapId The map.
   * @returns The arrival points, empty for a map the world lacks or a game that names no arrival type.
   */
  public arrivalsOn(mapId: string): ArrivalChoice[] {
    const map = this.host.document().maps.find((candidate) => candidate.id === mapId);
    return map === undefined ? [] : arrivalsOf(map, this.host.arrivalType());
  }

  /**
   * The words a host is shown by in a list: the host map's name and the arrival point's.
   *
   * @param host The host.
   * @returns For example "Willowmere / Inn door", or the ids when the map or point is missing.
   */
  public hostWords(host: MapHost): string {
    const map = this.host.document().maps.find((candidate) => candidate.id === host.map);
    const arrival = map === undefined ? undefined : arrivalsOf(map, this.host.arrivalType()).find((choice) => choice.id === host.arrival);
    return `${map?.name ?? host.map} / ${arrival?.name ?? host.arrival}`;
  }

  /**
   * The zone type a new room's floor is painted with.
   *
   * @returns The game's room zone, or null when it names none.
   */
  public get floorZone(): ZoneTypeDefinition | null {
    return this.host.roomZone();
  }

  /**
   * The music and ambience the game lists.
   *
   * @returns The lists.
   */
  public get atmosphereLists(): AtmosphereLists {
    return this.host.atmosphereLists();
  }

  /**
   * Why a new room may not be made from a plan, or null: the name, the size and the host, in that order, then the
   * rules of the document.
   *
   * @param plan The plan.
   * @returns The reason, or null.
   */
  public createRefusal(plan: RoomPlan): string | null {
    const world = this.host.document();
    const refusal = mapNameRefusal(world, plan.name) ?? roomSizeRefusal(plan.chunksWide, plan.chunksHigh, world.chunkSize) ?? this.hostRefusal(plan.host);
    if (refusal !== null) {
      return refusal;
    }
    if (plan.share !== "open" && plan.host === undefined) {
      return "A room shared by a party, one character or an owner needs a host";
    }
    return breach(applyCommand(world, this.createCommand(world, "map-probe", "layer-probe", plan)), "map-probe");
  }

  /**
   * Makes a room as one command and opens it, as a draft (ADR-122 decision 9): a map of the size in chunks with its
   * host, sharing and atmosphere, and, when asked and the game names a room zone, one zone layer "Safety" painted
   * with that zone over the whole floor (decision 3.1). One undo removes the room with its paint.
   *
   * @param plan The plan.
   * @returns The room's id.
   * @throws Error with the refusal's words when the plan is refused or the room was not added.
   */
  public create(plan: RoomPlan): string {
    const refusal = this.createRefusal(plan);
    if (refusal !== null) {
      throw new Error(refusal);
    }
    const world = this.host.document();
    const id = allocateMapId(world, this.host.idTag());
    this.host.forget(id);
    if (!this.host.run(this.createCommand(world, id, this.host.allocateId("layer"), plan))) {
      throw new Error("the room was not added");
    }
    this.host.open(id);
    return id;
  }

  /**
   * Why a host is refused, or null: it names a map on the grid and an arrival point on it.
   *
   * @param host The host, or undefined for none.
   * @returns The reason, or null.
   */
  public hostRefusal(host: MapHost | undefined): string | null {
    if (host === undefined) {
      return null;
    }
    const map = this.host.document().maps.find((candidate) => candidate.id === host.map);
    if (map === undefined || map.place === undefined) {
      return "A room is hosted on a map that stands on the grid";
    }
    return arrivalsOf(map, this.host.arrivalType()).some((choice) => choice.id === host.arrival) ? null : `${map.name} holds no such arrival point`;
  }

  /**
   * Why a map may not take a host, or null: the host's own rules, then the rules of the document with the change
   * applied. Nothing is recorded.
   *
   * @param mapId The map.
   * @param host The host, or undefined to take the map off its host.
   * @returns The reason, or null.
   * @throws Error when the world has no such map.
   */
  public setHostRefusal(mapId: string, host: MapHost | undefined): string | null {
    const map = this.require(mapId);
    if (host?.map === mapId) {
      return "A room cannot be hosted on itself";
    }
    const refusal = this.hostRefusal(host);
    if (refusal !== null) {
      return refusal;
    }
    try {
      return breach(applyCommand(this.host.document(), { kind: "compound", commands: hostCommands(map, host) }), mapId);
    } catch (error) {
      return plainly(error, mapId);
    }
  }

  /**
   * Makes a map a room, changes its host, or takes the host away, as one command that one undo takes back whole. A
   * map that stands on the grid leaves it first, in the same command.
   *
   * @param mapId The map.
   * @param host The host, or undefined for none.
   * @returns False when the map already has the host, and nothing was recorded.
   * @throws Error when the world has no such map, or with the refusal's words when the host is refused.
   */
  public setHost(mapId: string, host: MapHost | undefined): boolean {
    const map = this.require(mapId);
    if (map.host?.map === host?.map && map.host?.arrival === host?.arrival) {
      return false;
    }
    const refusal = this.setHostRefusal(mapId, host);
    if (refusal !== null) {
      throw new Error(refusal);
    }
    const commands = hostCommands(map, host);
    return this.host.run(commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands });
  }

  /**
   * Sets who shares a map as one command.
   *
   * @param mapId The map.
   * @param share The sharing.
   * @returns False when the map already has it, and nothing was recorded.
   * @throws Error when the world has no such map, or with the rule's words when a map without a host takes a
   *   sharing other than open, or a map on the grid takes any but open.
   */
  public setShare(mapId: string, share: MapShare): boolean {
    const map = this.require(mapId);
    const before = map.share ?? "open";
    if (before === share) {
      return false;
    }
    if (share !== "open" && map.host === undefined) {
      throw new Error("Only a room can be shared by a party, one character or an owner; give it a host first");
    }
    return this.host.run({ kind: "setMapShare", mapId, before, after: share });
  }

  /**
   * Sets a map's atmosphere as one command.
   *
   * @param mapId The map.
   * @param atmosphere The atmosphere; an empty one clears it.
   * @returns False when the map already has it, and nothing was recorded.
   * @throws Error when the world has no such map.
   */
  public setAtmosphere(mapId: string, atmosphere: MapAtmosphere): boolean {
    const map = this.require(mapId);
    const before = map.atmosphere ?? {};
    if (JSON.stringify(before) === JSON.stringify(atmosphere)) {
      return false;
    }
    return this.host.run({ kind: "setMapAtmosphere", mapId, before, after: atmosphere });
  }

  /**
   * The command that adds a room.
   *
   * @param world The world.
   * @param id The room's id.
   * @param layerId The floor layer's id.
   * @param plan The plan.
   * @returns The `addMap` command.
   */
  private createCommand(world: WorldDocument, id: string, layerId: string, plan: RoomPlan): Command {
    const width = plan.chunksWide * world.chunkSize;
    const height = plan.chunksHigh * world.chunkSize;
    const base = newMapDocument(world.id, id, plan.name.trim(), width, height);
    const zone = this.host.roomZone();
    const cells = width * height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
    const layers: MapDocument["layers"] = plan.floor && zone !== null ? [{ kind: "zone", id: layerId, name: "Safety", types: [zone.id], cells: new Uint8Array(cells).fill(1) }] : [];
    const atmosphere = plan.atmosphere !== undefined && Object.keys(plan.atmosphere).length > 0 ? { atmosphere: plan.atmosphere } : {};
    const map: MapDocument = { ...base, layers, draft: true, ...(plan.host === undefined ? {} : { host: plan.host }), ...(plan.share === "open" ? {} : { share: plan.share }), ...atmosphere };
    return { kind: "addMap", index: world.maps.length, map: layers.length === 0 ? map : { ...map, playersAbove: playersAbove(map) } };
  }

  /**
   * The map of an id.
   *
   * @param mapId The map.
   * @returns The map.
   * @throws Error when the world has no such map.
   */
  private require(mapId: string): MapDocument {
    const map = this.host.document().maps.find((candidate) => candidate.id === mapId);
    if (map === undefined) {
      throw new Error(`the world has no map ${mapId}`);
    }
    return map;
  }
}
